import React, { useEffect, useRef, useContext, useCallback, useState, useMemo } from 'react'
import { Typography } from 'antd'
import { useIntl } from 'react-intl'
import { Chart, registerables } from 'chart.js'
import { PiChartLineBold } from 'react-icons/pi'
import { DARK_THEME, lightThemeToken, darkThemeToken } from '@vacationtracker/shared/constants/ThemeSetting'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { ENERGY_MODE_BREAKPOINTS, getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { EnergyTrend, DepartmentTrend, PTOSuggestion, IFeedbackInfo } from '../../types'
import { ThemeContext } from '../../../../context/themeContext'
import { ENERGY_MODE_VISUAL_CONFIG } from '../../services/energy-mode-config'
import ChatBubble from '../../../../assets/images/chat-bubble40.png'

Chart.register(...registerables)

// Preload the feedback icon at module level so it's ready immediately
// Use a canvas to pre-render the icon for faster Chart.js rendering
const feedbackIcon = new Image(20, 20)
feedbackIcon.src = ChatBubble
let iconLoaded = false

// Create canvases to cache the rendered icon
let cachedIconCanvas: HTMLCanvasElement | null = null // For chart points with offset
let cachedIconCanvasLegend: HTMLCanvasElement | null = null // For legend without offset

// Create calculator icon for algorithm updates
const createCalculatorIcon = (color = '#4A9EFF'): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = 20
  canvas.height = 20
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.fillStyle = color
    // Draw calculator body (rectangle with rounded corners manually)
    ctx.beginPath()
    ctx.moveTo(3, 2)
    ctx.lineTo(17, 2)
    ctx.quadraticCurveTo(18, 2, 18, 3)
    ctx.lineTo(18, 17)
    ctx.quadraticCurveTo(18, 18, 17, 18)
    ctx.lineTo(3, 18)
    ctx.quadraticCurveTo(2, 18, 2, 17)
    ctx.lineTo(2, 3)
    ctx.quadraticCurveTo(2, 2, 3, 2)
    ctx.closePath()
    ctx.fill()
    // Draw screen
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(4, 4, 12, 5)
    // Draw buttons (3x3 grid)
    ctx.fillStyle = color
    const buttonSize = 2.5
    const spacing = 3.5
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        ctx.fillRect(5 + col * spacing, 11 + row * spacing, buttonSize, buttonSize)
      }
    }
  }
  return canvas
}

let calculatorIconCanvas: HTMLCanvasElement | null = null
let calculatorIconCanvasLegend: HTMLCanvasElement | null = null

// Create calculator icons immediately (don't wait for feedback icon)
calculatorIconCanvas = createCalculatorIcon('#4A9EFF')
calculatorIconCanvasLegend = createCalculatorIcon('#4A9EFF')

feedbackIcon.onload = () => {
  iconLoaded = true

  // Pre-render canvas for chart points with horizontal and vertical offset
  cachedIconCanvas = document.createElement('canvas')
  cachedIconCanvas.width = 35 // Extra width for horizontal offset
  cachedIconCanvas.height = 40 // Height for vertical positioning (icon at top half)
  const ctx = cachedIconCanvas.getContext('2d')
  if (ctx) {
    // Draw icon shifted 15px to the right, at top of canvas
    // Canvas center is at y=20, icon spans 0-20, so icon sits above the data point
    ctx.drawImage(feedbackIcon, 15, 0, 20, 20)
  }

  // Pre-render canvas for legend without offset
  cachedIconCanvasLegend = document.createElement('canvas')
  cachedIconCanvasLegend.width = 20 // Keep at 20px for legend to prevent text overlap
  cachedIconCanvasLegend.height = 20
  const ctxLegend = cachedIconCanvasLegend.getContext('2d')
  if (ctxLegend) {
    ctxLegend.drawImage(feedbackIcon, 0, 0, 20, 20)
  }
}

const { Title } = Typography

interface EnergyTrendChartProps {
  energyTrend: EnergyTrend[]
  departmentTrend: DepartmentTrend[]
  actionEffect: string
  bestPTOSuggestion?: PTOSuggestion
  trend: number
  feedbackInfo?: IFeedbackInfo
  energyModeNameMap: Record<EnergyMode, string>
  onTooltipView?: (tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string, elementHovered: string, section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    hoverDurationMs: number, tooltipTriggeredBy?: 'mouse_hover' | 'touch' | 'keyboard_focus', hoveredScore?: number, hoveredDate?: string,
    hoveredWeek?: number, hoveredYear?: number, chartDataset?: string) => void
  onActionTaken?: (actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click', actionName: string,
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion',
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', ledToNavigation?: boolean, destinationUrl?: string) => void
}

const EnergyTrendChart: React.FC<EnergyTrendChartProps> = ({
  energyTrend,
  departmentTrend,
  bestPTOSuggestion,
  trend,
  feedbackInfo,
  energyModeNameMap,
  onTooltipView,
}) => {
  const { formatMessage } = useIntl()
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstance = useRef<Chart | null>(null)
  const { theme } = useContext(ThemeContext)
  const hoverStartTime = useRef<number | null>(null)
  const currentHoveredElement = useRef<{ index: number; datasetIndex: number; value: number; label: string; datasetLabel: string } | null>(null)
  const [isFeedbackIconReady, setIsFeedbackIconReady] = useState(iconLoaded)

  // Memoize colors based on theme to prevent unnecessary recalculations
  const isDarkTheme = useMemo(() => theme === DARK_THEME, [theme])
  const primaryColor = useMemo(
    () => isDarkTheme ? darkThemeToken.token.colorPrimary : lightThemeToken.token.colorPrimary,
    [isDarkTheme]
  )

  // Create a stable chart data key that only changes when actual data changes
  // This prevents unnecessary re-renders when props have new references but same content
  const chartDataKey = useMemo(() => {
    const energyKey = energyTrend.map(item => `${item.date}:${item.score}`).join('|')
    const departmentKey = departmentTrend.map(item => `${item.date}:${item.score}`).join('|')
    const feedbackKey = feedbackInfo?.allFeedbacks?.map(f => `${f.timestamp}:${f.score}`).join('|') || ''
    const feedbackMetaKey = feedbackInfo ? `${feedbackInfo.weight}:${feedbackInfo.feedbackScore}:${feedbackInfo.latestFeedbackTimestamp}` : ''
    const modeMapKey = Object.entries(energyModeNameMap).sort().map(([k, v]) => `${k}:${v}`).join('|')
    return `${energyKey}|${departmentKey}|${feedbackKey}|${feedbackMetaKey}|${modeMapKey}|${theme}|${primaryColor}`
  }, [energyTrend, departmentTrend, feedbackInfo, energyModeNameMap, theme, primaryColor])

  const startTooltipTracking = useCallback((element: any) => {
    if (!onTooltipView) return

    const dataset = chartInstance.current?.data.datasets[element.datasetIndex]
    const label = chartInstance.current?.data.labels?.[element.index]
    const value = dataset?.data[element.index]

    if (dataset && label !== undefined && value !== null && value !== undefined) {
      // Check if we're already tracking this exact element
      const isSameElement = currentHoveredElement.current &&
        currentHoveredElement.current.index === element.index &&
        currentHoveredElement.current.datasetIndex === element.datasetIndex

      if (!isSameElement) {
        // Start tracking this element
        hoverStartTime.current = Date.now()
        currentHoveredElement.current = {
          index: element.index,
          datasetIndex: element.datasetIndex,
          value: Number(value),
          label: String(label),
          datasetLabel: dataset.label || '',
        }
      }
    }
  }, [onTooltipView])

  const endTooltipTracking = useCallback(() => {
    if (!onTooltipView || !hoverStartTime.current || !currentHoveredElement.current) return

    const hoverDuration = Date.now() - hoverStartTime.current
    const element = currentHoveredElement.current

    // Only send event if hover was meaningful (> 100ms)
    if (hoverDuration > 100) {
      // Determine tooltip type based on dataset
      const tooltipType = element.datasetLabel?.includes('Department') ? 'department_comparison' : 'trend_point'

      // Get the actual date from the energy trend data using the element index
      const allDataPoints = new Set<string>()
      energyTrend.forEach(item => allDataPoints.add(item.date))
      departmentTrend.forEach(item => allDataPoints.add(item.date))
      const sortedDates = Array.from(allDataPoints).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

      const actualDate = sortedDates[element.index]
      const date = actualDate ? new Date(actualDate) : new Date()
      const weekNumber = getWeekNumber(date)
      const year = date.getFullYear()

      // Create enhanced tooltip content with week information
      const tooltipContent = `Week ${weekNumber} ${year}: ${element.datasetLabel} was ${element.value} points on ${element.label}`

      // Create element identifier with week and score
      const elementHovered = `chart_week_${weekNumber}_${year}_score_${element.value}_dataset_${element.datasetIndex}`

      onTooltipView(
        tooltipType,
        tooltipContent,
        elementHovered,
        'trend_chart',
        hoverDuration, // Actual hover duration
        'mouse_hover', // tooltipTriggeredBy
        element.value, // hoveredScore
        actualDate || element.label, // hoveredDate - use actual date
        weekNumber, // hoveredWeek
        year, // hoveredYear
        element.datasetLabel // chartDataset
      )
    }

    // Reset tracking
    hoverStartTime.current = null
    currentHoveredElement.current = null
  }, [onTooltipView, energyTrend, departmentTrend])

  const getWeekNumber = (date: Date): number => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
  }

  // Set up listener for icon load if it hasn't loaded yet
  useEffect(() => {
    if (!isFeedbackIconReady && !iconLoaded) {
      const handleLoad = () => {
        iconLoaded = true
        // Wait a tiny bit for the canvas to be rendered
        setTimeout(() => {
          setIsFeedbackIconReady(true)
        }, 0)
      }
      feedbackIcon.addEventListener('load', handleLoad)
      // Check if already loaded (in case it loaded before event listener was attached)
      if (feedbackIcon.complete) {
        handleLoad()
      }
      return () => {
        feedbackIcon.removeEventListener('load', handleLoad)
      }
    }
  }, [isFeedbackIconReady])

  useEffect(() => {
    // If there's feedback data, wait for the icon to load before rendering
    const hasFeedback = feedbackInfo && feedbackInfo.allFeedbacks && feedbackInfo.allFeedbacks.length > 0
    if (hasFeedback && !isFeedbackIconReady) {
      return // Wait for icon to load
    }

    if (chartRef.current && energyTrend.length > 0) {
      const ctx = chartRef.current.getContext('2d')
      if (ctx) {
        // Destroy existing chart
        if (chartInstance.current) {
          chartInstance.current.destroy()
        }

        // Use energy mode name map passed from parent component
        const energyModeToLabel = energyModeNameMap

        // Map breakpoint values to energy modes for efficient lookup
        const breakpointToModeMap = new Map([
          [ENERGY_MODE_BREAKPOINTS.PEAK, EnergyMode.PEAK],
          [ENERGY_MODE_BREAKPOINTS.ENERGIZED, EnergyMode.ENERGIZED],
          [ENERGY_MODE_BREAKPOINTS.FUNCTIONAL, EnergyMode.FUNCTIONAL],
          [ENERGY_MODE_BREAKPOINTS.STRUGGLING, EnergyMode.STRUGGLING],
          [ENERGY_MODE_BREAKPOINTS.DRAINED, EnergyMode.DRAINED],
        ])

        // Helper function to get color for energy mode (resolves CSS variables to computed values)
        const getColorForEnergyMode = (mode: EnergyMode, element?: HTMLElement): string => {
          const config = ENERGY_MODE_VISUAL_CONFIG[mode]
          if (!config?.primaryColor) {
            return '#000000'
          }

          // If it's a CSS variable, get the computed value
          if (config.primaryColor.startsWith('var(')) {
            const cssVar = config.primaryColor.replace(/var\(|\)/g, '').trim()
            if (typeof window !== 'undefined') {
              const elementToUse = element || chartRef.current || document.body
              const computedStyle = window.getComputedStyle(elementToUse)
              const color = computedStyle.getPropertyValue(cssVar).trim()
              return color || '#000000'
            }
          }
          return config.primaryColor
        }

        // Helper function to convert color to hex with opacity (8-digit hex format)
        const colorToHexWithOpacity = (color: string, opacity: number): string => {
          // Convert opacity (0-1) to hex (00-FF)
          const opacityHex = Math.round(opacity * 255).toString(16).padStart(2, '0').toUpperCase()

          if (color.startsWith('#')) {
            const hex = color.slice(1)
            // If already 8-digit hex, replace alpha; otherwise append
            if (hex.length === 8) {
              return `#${hex.slice(0, 6)}${opacityHex}`
            }
            return `#${hex}${opacityHex}`
          }

          // Fallback
          return color
        }

        // Helper function to normalize timestamps to date-only strings (YYYY-MM-DD)
        const normalizeToDate = (timestamp: string): string => {
          return new Date(timestamp).toISOString().split('T')[0]
        }

        // Create maps for quick lookup with normalized dates
        const userEnergyMap = new Map(energyTrend.map(item => [normalizeToDate(item.date), item.score]))
        const departmentMap = new Map(departmentTrend.map(item => [normalizeToDate(item.date), item.score]))

        // Helper function to find the most recent calculation date before or equal to feedback timestamp
        const findPreviousCalculationDate = (feedbackTimestamp: string): string | null => {
          const feedbackDate = new Date(feedbackTimestamp).getTime()
          const calculationDates = Array.from(userEnergyMap.keys())
            .map(date => ({ date, timestamp: new Date(date).getTime() }))
            .filter(({ timestamp }) => timestamp <= feedbackDate)
            .sort((a, b) => b.timestamp - a.timestamp) // Sort descending to get most recent first

          return calculationDates.length > 0 ? calculationDates[0].date : null
        }

        // Map feedback to previous calculation dates
        // Maps calculation date to the most recent feedback score for that calculation
        const feedbackByCalculationDate = new Map<string, { score: number; timestamp: string }>()
        if (feedbackInfo && feedbackInfo.allFeedbacks) {
          feedbackInfo.allFeedbacks.forEach(feedback => {
            const calculationDate = findPreviousCalculationDate(feedback.timestamp)
            if (calculationDate) {
              const existing = feedbackByCalculationDate.get(calculationDate)
              // If no existing feedback for this calculation date, or this feedback is more recent, use this one
              if (!existing || new Date(feedback.timestamp) > new Date(existing.timestamp)) {
                feedbackByCalculationDate.set(calculationDate, {
                  score: feedback.score,
                  timestamp: feedback.timestamp,
                })
              }
            }
          })
        }

        // Create a unified timeline that includes all data points from both user and department
        // Using normalized dates (YYYY-MM-DD) to ensure same-day entries share the same x-axis label
        const allDataPoints = new Set<string>()

        // Add all user energy trend dates (normalized)
        energyTrend.forEach(item => allDataPoints.add(normalizeToDate(item.date)))

        // Add all department trend dates (normalized)
        departmentTrend.forEach(item => allDataPoints.add(normalizeToDate(item.date)))

        // Add calculation dates that have feedback (they should already be in the timeline, but ensure they are)
        feedbackByCalculationDate.forEach((_, calculationDate) => {
          allDataPoints.add(calculationDate)
        })

        // Sort all dates chronologically
        const sortedDates = Array.from(allDataPoints).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

        const labels = sortedDates.map(dateStr => {
          const date = new Date(dateStr)
          // Always show just the date
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        })

        // Map user energy data to the unified timeline (use ?? to preserve 0 values)
        const userData = sortedDates.map(date => userEnergyMap.get(date) ?? null)

        // Map department data to the unified timeline (use ?? to preserve 0 values)
        const departmentData = sortedDates.map(date => departmentMap.get(date) ?? null)

        // Create datasets array - department first (background), then user score (foreground)
        const datasets: any[] = []

        // Add department trend first if available - light gray line with circles
        if (departmentTrend.length > 0) {
          const isDarkTheme = theme === DARK_THEME
          datasets.push({
            label: formatMessage({ id: 'energyLevel.chart.departmentAverage' }),
            data: departmentData,
            borderColor: isDarkTheme ? 'rgba(208, 208, 207, 0.5)' : '#D0D0CF', // Light gray
            backgroundColor: isDarkTheme ? 'rgba(208, 208, 207, 0.5)' : '#D0D0CF',
            borderWidth: 2,
            fill: false,
            tension: 0,
            pointRadius: 4,
            pointBackgroundColor: isDarkTheme ? 'rgba(208, 208, 207, 0.5)' : '#D0D0CF',
            pointBorderColor: isDarkTheme ? 'rgba(208, 208, 207, 0.5)' : '#D0D0CF',
            spanGaps: true, // Connect points even when there are gaps
            order: 2, // Render behind user score
          })
        }

        // Add user energy score last so it renders on top - purple color
        datasets.push({
          label: formatMessage({ id: 'energyLevel.chart.yourEnergyLevel' }),
          data: userData,
          borderColor: primaryColor,
          backgroundColor: primaryColor,
          borderWidth: 2,
          fill: false,
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: primaryColor,
          pointBorderColor: primaryColor,
          spanGaps: true, // Connect points even when there are gaps
          order: 1, // Render on top
        })

        // Add feedback scores if available - as disconnected points
        if (feedbackInfo && feedbackInfo.allFeedbacks && feedbackInfo.allFeedbacks.length > 0) {
          // Map feedback data to the unified timeline using calculation dates (not feedback timestamps)
          // Use nullish coalescing (??) to preserve 0 values (0 || null would return null)
          const feedbackData = sortedDates.map(date => {
            const feedback = feedbackByCalculationDate.get(date)
            return feedback ? feedback.score : null
          })

          datasets.push({
            id: 'feedback', // Custom identifier for tooltip filtering
            label: formatMessage({ id: 'energyLevel.chart.yourFeedback' }),
            data: feedbackData,
            borderColor: '#EA6907', // Orange color for feedback (matching struggling energy level)
            backgroundColor: '#EA6907',
            borderWidth: 0,
            fill: false,
            showLine: false, // Don't connect the dots
            pointRadius: 2, // Make dots visible
            pointHitRadius: 20, // Larger hit area for easier hovering over icons
            pointHoverRadius: 2, // Keep same size on hover
            pointStyle: cachedIconCanvas || feedbackIcon, // Use pre-rendered canvas for faster drawing
            spanGaps: false,
            order: 0, // Render on top of everything
          })
        }

        // Add algorithm update annotations if available - positioned below X-axis
        // TODO: Add algorithmUpdateDates prop when backend provides this data
        // For now, this is a placeholder structure
        const algorithmUpdateDates: string[] = [] // This would come from props/API
        if (algorithmUpdateDates.length > 0 && calculatorIconCanvas) {
          const algorithmMap = new Map(algorithmUpdateDates.map(date => [normalizeToDate(date), -5])) // Use negative value to position below axis
          const algorithmData = sortedDates.map(date => algorithmMap.get(date) ?? null)

          datasets.push({
            label: formatMessage({ id: 'energyLevel.chart.algorithmUpdate' }),
            data: algorithmData,
            borderColor: '#4A9EFF', // Light blue color
            backgroundColor: '#4A9EFF',
            borderWidth: 0,
            fill: false,
            showLine: false,
            pointRadius: 2,
            pointHitRadius: 20,
            pointHoverRadius: 2,
            pointStyle: calculatorIconCanvas,
            spanGaps: false,
            order: 0,
          })
        }

        chartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            clip: false, // Don't clip elements at chart edges
            interaction: {
              mode: 'point',
              intersect: false,
              axis: 'x',
              includeInvisible: false,
            },
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  usePointStyle: true,
                  padding: 15, // Padding between legend items
                  boxWidth: 30, // Width allocated for the icon
                  boxHeight: 20,
                  font: {
                    size: 12,
                  },
                  generateLabels: (chart: any) => {
                    return chart.data.datasets.map((dataset: any, index: number) => {
                      const isFeedback = dataset.label && dataset.label.toLowerCase().includes('feedback')
                      const isAlgorithmUpdate = dataset.label && dataset.label.toLowerCase().includes('algorithm')

                      let pointStyle: any = 'circle'
                      if (isFeedback) {
                        pointStyle = cachedIconCanvasLegend || feedbackIcon
                      } else if (isAlgorithmUpdate) {
                        pointStyle = calculatorIconCanvasLegend || calculatorIconCanvas
                      }

                      return {
                        text: dataset.label,
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        hidden: !chart.isDatasetVisible(index),
                        datasetIndex: index,
                        pointStyle,
                      }
                    })
                  },
                },
                onClick: (e: any, legendItem: any, legend: any) => {
                  const index = legendItem.datasetIndex
                  const chart = legend.chart
                  const meta = chart.getDatasetMeta(index)

                  // Toggle visibility
                  meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null

                  // Update chart without animation for instant show/hide
                  chart.update('none')
                },
              },
              tooltip: {
                enabled: true,
                padding: 10,
                boxPadding: 5,
                filter: (tooltipItem: any) => {
                  // Only show tooltip for feedback dataset (identified by custom id)
                  return tooltipItem.dataset?.id === 'feedback'
                },
                callbacks: {
                  title: () => '',
                  label: (tooltipItem: any) => {
                    const score = tooltipItem.raw as number
                    const energyMode = getEnergyMode(score)
                    const modeName = energyModeToLabel[energyMode] || ''
                    return formatMessage({
                      id: 'energyLevel.chart.youReported',
                    }, {
                      mode: modeName,
                    })
                  },
                },
              },
            },
            scales: {
              x: {
                offset: false, // Add offset to create space at the edges
                grid: {
                  display: true,
                  color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
              },
              y: {
                min: 0,
                max: 100,
                afterBuildTicks: function (axis) {
                  // Only show ticks at specific breakpoint values
                  axis.ticks = [
                    { value: ENERGY_MODE_BREAKPOINTS.PEAK },
                    { value: ENERGY_MODE_BREAKPOINTS.ENERGIZED },
                    { value: ENERGY_MODE_BREAKPOINTS.FUNCTIONAL },
                    { value: ENERGY_MODE_BREAKPOINTS.STRUGGLING },
                    { value: ENERGY_MODE_BREAKPOINTS.DRAINED },
                  ]
                },
                ticks: {
                  stepSize: undefined, // Remove stepSize to allow custom tick positions
                  color: function (context: any) {
                    const value = context.tick.value
                    const mode = breakpointToModeMap.get(value)
                    if (mode) {
                      const chartElement = context.chart?.canvas || chartRef.current
                      return getColorForEnergyMode(mode, chartElement)
                    }
                    return isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                  },
                  font: {
                    size: 12,
                    weight: '500',
                  },
                  callback: function (value: any) {
                    // Only show labels for specific energy mode breakpoints
                    if (breakpointToModeMap.has(value)) {
                      const mode = breakpointToModeMap.get(value)!
                      return energyModeToLabel[mode] || ''
                    }
                    return ''
                  },
                  maxTicksLimit: 5, // Limit to 5 ticks
                },
                grid: {
                  display: true,
                  color: function (context: any) {
                    const value = context.tick.value
                    const mode = breakpointToModeMap.get(value)
                    if (mode) {
                      const chartElement = context.chart?.canvas || chartRef.current
                      const color = getColorForEnergyMode(mode, chartElement)
                      return colorToHexWithOpacity(color, 0.38)
                    }
                    return isDarkTheme ? '#FFFFFF1A' : '#0000001A'
                  },
                },
              },
            },
            layout: {
              padding: {
                top: 20, // Extra padding at top for icons at max value
                bottom: 15, // Extra padding at bottom for icons at score 0
                right: 20, // Extra padding at right for icons at last date (5px offset)
                left: 10,
              },
            },
            onHover: (event: any, elements: any[]) => {
              if (elements.length > 0) {
                // Start tracking tooltip hover for the first element (only if not already tracking same element)
                startTooltipTracking(elements[0])
              } else {
                // End tracking when user moves away from chart - this fires the event
                endTooltipTracking()
              }
            },
          },
        })
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [chartDataKey, isFeedbackIconReady, formatMessage])

  return (
    <div className="energy-trend-chart">
      <Title level={4}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <PiChartLineBold style={{ verticalAlign: 'middle' }} />
          <IntlMessages id="energyLevel.chart.title" />
        </span>
      </Title>

      <div style={{ height: 376 }}>
        <canvas ref={chartRef} style={{ backgroundColor: 'transparent' }} />
      </div>

      { // Temporary disabled action effect until we adjust the recommendation logic
        /* {actionEffect && (
        <Alert
          message={actionEffect}
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )} */}
    </div>
  )
}

export default EnergyTrendChart
